import React from "react";
import { Flame } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { useGamification } from "../../contexts/GamificationContext";
import { useLanguage } from "../../App";

const StreakCounter = ({ size = "md", showLabel = true, className = "" }) => {
  const { streak, loading } = useGamification();
  const { language } = useLanguage();
  const isEnglish = language === "en";

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center animate-pulse"></div>
            <div className="space-y-1">
              <div className="h-4 bg-muted rounded animate-pulse w-24"></div>
              <div className="h-3 bg-muted rounded animate-pulse w-16"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-14 h-14"
  };

  const iconSizes = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-7 h-7"
  };

  return (
    <Card className={className}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          {/* Fire icon with animation */}
          <div
            className={`${sizeClasses[size]} rounded-full flex items-center justify-center transition-all ${
              streak > 0
                ? "bg-gradient-to-br from-primary to-primary/80"
                : "bg-gray-200"
            } ${streak >= 7 ? "animate-pulse" : ""}`}
          >
            <Flame
              className={`${iconSizes[size]} ${
                streak > 0 ? "text-white" : "text-gray-400"
              }`}
            />
          </div>

          <div className="flex-1">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold" style={{ color: streak > 0 ? "hsl(var(--primary))" : "#9CA3AF" }}>
                {streak}
              </span>
              {showLabel && (
                <span className="text-sm text-muted-foreground">
                  {isEnglish ? "day streak" : "hari streak"}
                </span>
              )}
            </div>
            {streak > 0 && (
              <p className="text-xs text-muted-foreground">
                {isEnglish ? "Keep it going!" : "Terus semangat!"}
                {streak >= 7 && (
                  <span className="ml-1 font-semibold text-primary">
                    {isEnglish ? "🔥 On fire!" : "🔥 Menggelegar!"}
                  </span>
                )}
              </p>
            )}
          </div>
        </div>

        {/* Weekly progress dots */}
        {streak > 0 && (
          <div className="flex gap-1.5 mt-3 justify-center">
            {Array.from({ length: 7 }).map((_, i) => {
              const dayInStreak = i < (streak % 7);
              return (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-all ${
                    dayInStreak ? "bg-primary" : "bg-gray-200"
                  }`}
                />
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StreakCounter;
